import type { SupabaseClient } from "@supabase/supabase-js";
import type { BreedingCycleState } from "../domain/breeding";
import type { BreederSex, QuantityPrecision, TankFamily } from "../domain/inventory";

export interface TankView {
  id: string;
  tankCode: string;
  tankFamily: TankFamily;
  active: boolean;
  domainVersion: number;
}

export interface BreederView {
  id: string;
  breederCode: string;
  sex: BreederSex;
  status: "active" | "retired" | "deceased" | "exited";
  color?: string;
  visualDescription?: string;
  photoUrl?: string;
  originPairId?: string;
  variety?: string;
  sourceFryBatchId?: string;
  sourceStockGroupId?: string;
  domainVersion: number;
}

export interface PairView {
  id: string;
  pairCode: string;
  maleBreederId: string;
  femaleBreederId: string;
  status: "active" | "ended";
  formedAt: string;
  endedAt?: string;
  endReason?: string;
  notes?: string;
  domainVersion: number;
}

export interface BreedingCycleView {
  id: string;
  pairId: string;
  spawnTankId: string;
  cycleNo: number;
  state: BreedingCycleState;
  spawnedAt: string;
  eggResult?: "hatched" | "partial_hatch" | "eaten" | "white";
  hatchPercent?: number;
  freeSwimAt?: string;
  parentFeedingAt?: string;
  separatedAt?: string;
  growoutAt?: string;
  completedAt?: string;
  failedAt?: string;
  failureReason?: string;
  domainVersion: number;
}

export interface FryBatchView {
  id: string;
  batchCode: string;
  sourcePairId?: string;
  sourceBreedingCycleId?: string;
  status: "active" | "closed";
  domainVersion: number;
}

export interface StockGroupView {
  id: string;
  groupCode?: string;
  fryBatchId: string;
  parentStockGroupId?: string;
  quantity?: number;
  quantityPrecision: QuantityPrecision;
  status: "active" | "closed";
  domainVersion: number;
}

export interface OccupancyView {
  id: string;
  tankId: string;
  occupantKind: "breeder" | "pair" | "stock_group";
  breederId?: string;
  pairId?: string;
  stockGroupId?: string;
  startedAt: string;
}

export interface BreedingInventorySnapshot {
  farmId: string;
  capturedAt: string;
  tanks: TankView[];
  breeders: BreederView[];
  pairs: PairView[];
  cycles: BreedingCycleView[];
  fryBatches: FryBatchView[];
  stockGroups: StockGroupView[];
  occupancies: OccupancyView[];
}

const CACHE_PREFIX = "discus.v2.breeding.inventory:";

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): StorageLike | undefined {
  return typeof localStorage === "undefined" ? undefined : localStorage;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  const number = Number(value);
  return value === null || value === undefined || !Number.isFinite(number) ? undefined : number;
}

export function stockQuantityLabel(stock: {
  quantity?: number;
  quantityPrecision: QuantityPrecision;
}): string {
  if (stock.quantityPrecision === "unknown" || stock.quantity === undefined) return "نامشخص";
  return stock.quantityPrecision === "estimated"
    ? `حدود ${stock.quantity.toLocaleString("fa-IR")}`
    : stock.quantity.toLocaleString("fa-IR");
}

export function cycleHistoryForPair(
  cycles: readonly BreedingCycleView[],
  pairId: string,
): BreedingCycleView[] {
  return cycles
    .filter((cycle) => cycle.pairId === pairId)
    .slice()
    .sort((left, right) => right.cycleNo - left.cycleNo);
}

export function writeBreedingInventoryCache(
  snapshot: BreedingInventorySnapshot,
  storage: StorageLike | undefined = defaultStorage(),
): void {
  storage?.setItem(`${CACHE_PREFIX}${snapshot.farmId}`, JSON.stringify(snapshot));
}

export function readBreedingInventoryCache(
  farmId: string,
  storage: StorageLike | undefined = defaultStorage(),
): BreedingInventorySnapshot | undefined {
  const raw = storage?.getItem(`${CACHE_PREFIX}${farmId}`);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as BreedingInventorySnapshot;
    return parsed?.farmId === farmId ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export async function loadBreedingInventorySnapshot(
  client: SupabaseClient,
  input: { farmId: string; observedAt?: Date },
): Promise<BreedingInventorySnapshot> {
  const observedAt = input.observedAt ?? new Date();

  const [
    tanksResult,
    breedersResult,
    pairsResult,
    occupanciesResult,
    cyclesResult,
    batchesResult,
    stockResult,
  ] = await Promise.all([
    client
      .from("tanks")
      .select("id,tank_code,tank_family,is_active,domain_version")
      .eq("farm_id", input.farmId)
      .order("tank_family")
      .order("tank_number"),
    client
      .from("breeder_fish")
      .select(
        "id,breeder_code,sex,status,color,visual_description,photo_url,origin_pair_id,variety,source_fry_batch_id,source_stock_group_id,domain_version",
      )
      .eq("farm_id", input.farmId)
      .order("breeder_code"),
    client
      .from("breeding_pairs")
      .select(
        "id,pair_code,male_breeder_id,female_breeder_id,status,formed_at,ended_at,end_reason,notes,domain_version",
      )
      .eq("farm_id", input.farmId)
      .order("formed_at", { ascending: false }),
    client
      .from("tank_occupancies")
      .select("id,tank_id,occupant_kind,breeder_id,pair_id,stock_group_id,started_at")
      .eq("farm_id", input.farmId),
    client
      .from("breeding_cycles")
      .select(
        "id,pair_id,spawn_tank_id,cycle_no,state,spawned_at,egg_result,hatch_percent,free_swim_at,parent_feeding_at,separated_at,growout_at,completed_at,failed_at,failure_reason,domain_version",
      )
      .eq("farm_id", input.farmId)
      .order("spawned_at", { ascending: false }),
    client
      .from("fry_batches")
      .select("id,batch_code,source_pair_id,source_breeding_cycle_id,status,domain_version")
      .eq("farm_id", input.farmId)
      .order("created_at", { ascending: false }),
    client
      .from("stock_groups")
      .select(
        "id,group_code,fry_batch_id,parent_stock_group_id,quantity,quantity_precision,status,domain_version",
      )
      .eq("farm_id", input.farmId)
      .order("created_at", { ascending: false }),
  ]);

  for (const result of [
    tanksResult,
    breedersResult,
    pairsResult,
    occupanciesResult,
    cyclesResult,
    batchesResult,
    stockResult,
  ]) {
    if (result.error) throw result.error;
  }

  return {
    farmId: input.farmId,
    capturedAt: observedAt.toISOString(),
    tanks: (tanksResult.data ?? []).map((row) => ({
      id: String(row.id),
      tankCode: String(row.tank_code),
      tankFamily: String(row.tank_family) as TankFamily,
      active: Boolean(row.is_active),
      domainVersion: Number(row.domain_version),
    })),
    breeders: (breedersResult.data ?? []).map((row) => ({
      id: String(row.id),
      breederCode: String(row.breeder_code),
      sex: String(row.sex) as BreederSex,
      status: String(row.status) as BreederView["status"],
      ...(optionalString(row.color) ? { color: String(row.color) } : {}),
      ...(optionalString(row.visual_description)
        ? { visualDescription: String(row.visual_description) }
        : {}),
      ...(optionalString(row.photo_url) ? { photoUrl: String(row.photo_url) } : {}),
      ...(optionalString(row.origin_pair_id) ? { originPairId: String(row.origin_pair_id) } : {}),
      ...(optionalString(row.variety) ? { variety: String(row.variety) } : {}),
      ...(optionalString(row.source_fry_batch_id)
        ? { sourceFryBatchId: String(row.source_fry_batch_id) }
        : {}),
      ...(optionalString(row.source_stock_group_id)
        ? { sourceStockGroupId: String(row.source_stock_group_id) }
        : {}),
      domainVersion: Number(row.domain_version),
    })),
    pairs: (pairsResult.data ?? []).map((row) => ({
      id: String(row.id),
      pairCode: String(row.pair_code),
      maleBreederId: String(row.male_breeder_id),
      femaleBreederId: String(row.female_breeder_id),
      status: String(row.status) as PairView["status"],
      formedAt: String(row.formed_at),
      ...(optionalString(row.ended_at) ? { endedAt: String(row.ended_at) } : {}),
      ...(optionalString(row.end_reason) ? { endReason: String(row.end_reason) } : {}),
      ...(optionalString(row.notes) ? { notes: String(row.notes) } : {}),
      domainVersion: Number(row.domain_version),
    })),
    cycles: (cyclesResult.data ?? []).map((row) => ({
      id: String(row.id),
      pairId: String(row.pair_id),
      spawnTankId: String(row.spawn_tank_id),
      cycleNo: Number(row.cycle_no),
      state: String(row.state) as BreedingCycleView["state"],
      spawnedAt: String(row.spawned_at),
      ...(optionalString(row.egg_result)
        ? { eggResult: String(row.egg_result) as NonNullable<BreedingCycleView["eggResult"]> }
        : {}),
      ...(optionalNumber(row.hatch_percent) === undefined
        ? {}
        : { hatchPercent: Number(row.hatch_percent) }),
      ...(optionalString(row.free_swim_at) ? { freeSwimAt: String(row.free_swim_at) } : {}),
      ...(optionalString(row.parent_feeding_at)
        ? { parentFeedingAt: String(row.parent_feeding_at) }
        : {}),
      ...(optionalString(row.separated_at) ? { separatedAt: String(row.separated_at) } : {}),
      ...(optionalString(row.growout_at) ? { growoutAt: String(row.growout_at) } : {}),
      ...(optionalString(row.completed_at) ? { completedAt: String(row.completed_at) } : {}),
      ...(optionalString(row.failed_at) ? { failedAt: String(row.failed_at) } : {}),
      ...(optionalString(row.failure_reason) ? { failureReason: String(row.failure_reason) } : {}),
      domainVersion: Number(row.domain_version),
    })),
    fryBatches: (batchesResult.data ?? []).map((row) => ({
      id: String(row.id),
      batchCode: String(row.batch_code),
      ...(optionalString(row.source_pair_id) ? { sourcePairId: String(row.source_pair_id) } : {}),
      ...(optionalString(row.source_breeding_cycle_id)
        ? { sourceBreedingCycleId: String(row.source_breeding_cycle_id) }
        : {}),
      status: String(row.status) as FryBatchView["status"],
      domainVersion: Number(row.domain_version),
    })),
    stockGroups: (stockResult.data ?? []).map((row) => ({
      id: String(row.id),
      ...(optionalString(row.group_code) ? { groupCode: String(row.group_code) } : {}),
      fryBatchId: String(row.fry_batch_id),
      ...(optionalString(row.parent_stock_group_id)
        ? { parentStockGroupId: String(row.parent_stock_group_id) }
        : {}),
      ...(optionalNumber(row.quantity) === undefined ? {} : { quantity: Number(row.quantity) }),
      quantityPrecision: String(row.quantity_precision) as QuantityPrecision,
      status: String(row.status) as StockGroupView["status"],
      domainVersion: Number(row.domain_version),
    })),
    occupancies: (occupanciesResult.data ?? []).map((row) => ({
      id: String(row.id),
      tankId: String(row.tank_id),
      occupantKind: String(row.occupant_kind) as OccupancyView["occupantKind"],
      ...(optionalString(row.breeder_id) ? { breederId: String(row.breeder_id) } : {}),
      ...(optionalString(row.pair_id) ? { pairId: String(row.pair_id) } : {}),
      ...(optionalString(row.stock_group_id) ? { stockGroupId: String(row.stock_group_id) } : {}),
      startedAt: String(row.started_at),
    })),
  };
}
